// @ts-nocheck
import React, { useState, useEffect, useContext } from 'react';
import { useRouter } from 'next/router';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  IconBriefcase,
  IconChevronRight, IconBrandWhatsapp, IconPrinter, IconLoader2, IconCreditCard, IconMessageCircle,
} from '@tabler/icons-react';
import moment from 'moment';
import Fetch from '@/utils/Fetch';
import Helper from '@/utils/helper';
import * as Enums from '@/utils/enums';
import PageContext from '@/utils/page-context';
import { useCustomerZoneCompany } from '@/utils/useCustomerZoneCompany';
import DownloadService from '@/utils/download-service';
import { getCustomerZoneItemPayments } from '@/PageComponents/Payments/payments';
import PaymentService from '@/services/payment-service';
import { roundPrice } from '@/utils/line-totals';
import { getGroupedItems } from '@/PageComponents/SectionTable/SectionTable';
import { StatusPill, invoiceStatusDisplay, deriveStatusTone } from '../CustomerZoneShared';
import CustomerZoneMessageModal from '../CustomerZoneMessageModal';
import styles from '../CustomerZoneDetail.module.css';

export default function CustomerZoneInvoiceDetail() {
  const router = useRouter();
  const { t, c, i, com, fromJobId, fromJobNum } = router.query;
  const pageContext = useContext(PageContext) as any;
  const [printing, setPrinting] = useState(false);
  const [payNowLink, setPayNowLink] = useState<string | null>(null);
  const [showMessage, setShowMessage] = useState(false);

  const { data: tenantData } = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t }),
    { enabled: !!t }
  );
  const tenant = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0
    ? tenantData.Results[0] : null;

  useEffect(() => {
    if (tenantData && !tenant) router.replace('/');
  }, [tenantData]);

  const { data: company } = useCustomerZoneCompany(t, c, tenant?.API);

  const { data: invoiceData } = useQuery(
    ['czInvoice', t, c, i],
    () => Fetch.get({ url: '/CustomerZone/GetInvoice', params: { invoiceID: i }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant && !!i }
  );

  const invoice = invoiceData || null;

  const linkedJobId = invoice?.Module === Enums.Module.JobCard ? invoice.ItemID : null;
  const { data: linkedJobData } = useQuery(
    ['czInvoiceLinkedJob', t, c, linkedJobId],
    () => Fetch.get({ url: '/CustomerZone/GetJob', params: { jobCardID: linkedJobId }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant && !!linkedJobId }
  );

  const { data: paymentsRaw } = useQuery(
    ['czInvoicePayments', t, c, i],
    () => getCustomerZoneItemPayments(invoice.ID, Enums.Module.Invoice, t, c, tenant.API),
    { enabled: !!invoice }
  );
  const payments = Array.isArray(paymentsRaw) ? paymentsRaw : (paymentsRaw?.Results || []);

  useEffect(() => {
    if (invoiceData?.headerTenantName) pageContext?.updateTenantName?.(invoiceData.headerTenantName);
  }, [invoiceData]);

  useEffect(() => {
    if (!invoice || !tenant) return;
    const totalPaid = payments.reduce((s: number, p: any) => s + +p.Amount, 0);
    const due = Math.max(0, roundPrice(+invoice.TotalInclusive - totalPaid));
    PaymentService.getPayNowLink(invoice.ID, invoice.InvoiceNumber, Enums.Module.Invoice, due, t, c, tenant.API)
      .then((url: string | null) => setPayNowLink(url));
  }, [invoice, tenant, payments]);

  useEffect(() => {
    if (payNowLink && com === 'paynow' &&
      (invoice?.InvoiceStatus === Enums.InvoiceStatus.Unpaid || invoice?.InvoiceStatus === Enums.InvoiceStatus.Overdue)) {
      window.location.href = payNowLink;
    }
  }, [payNowLink]);

  if (!tenant || !invoice) return null;

  const currencySymbol = company?.Currency?.Symbol || '';
  const taxPercentage = company?.TaxPercentage;
  const waPhone = company?.WhatsAppNumber
    ? company.WhatsAppNumber.replace(/\D/g, '').replace(/^0/, '27') : null;
  const base = `?t=${t}&c=${c}`;
  const logoUrl = company?.LogoUrl || null;
  const companyName = company?.Name || tenant?.Name || '';
  const { label: statusLabel, tone } = invoiceStatusDisplay(invoice.InvoiceStatus);
  const groupedItems = getGroupedItems(
    (invoice.InvoiceItems || []).map((x: any, idx: number) => ({
      ...x, groupId: x.InventorySectionID || `solo-${idx}-${x.ID}`
    })).sort((a: any, b: any) => a.LineNumber - b.LineNumber),
    'InventorySectionName', 'InventorySectionID'
  );

  const contactName = invoice.Contact
    ? `${invoice.Contact.FirstName || ''} ${invoice.Contact.LastName || ''}`.trim()
    : invoice.CustomerName || '';

  const lessPayments = payments.reduce((s: number, p: any) => s + +p.Amount, 0);
  const amountDue = Math.max(0, roundPrice(+invoice.TotalInclusive - lessPayments));

  const bankName = invoice.Store?.BankName || company?.BankName;
  const bankAccountName = invoice.Store?.BankAccountName || company?.BankAccountName;
  const bankAccountNumber = invoice.Store?.BankAccountNumber || company?.BankAccountNumber;
  const bankBranchCode = invoice.Store?.BankBranchCode || company?.BankBranchCode;
  const hasBankDetails = !!(bankName || bankAccountName || bankAccountNumber);

  const linkedJob = linkedJobData?.ID ? linkedJobData : null;

  const handleDownload = async () => {
    setPrinting(true);
    try {
      await DownloadService.downloadFile(
        'GET', `/CustomerZone/GetInvoiceDocument?invoiceID=${invoice.ID}`,
        null, true, false, t, c, tenant.API, false
      );
    } finally {
      setPrinting(false);
    }
  };

  const payNow = () => { if (payNowLink) window.location.href = payNowLink; };

  const handleWhatsApp = () => {
    if (!waPhone) return;
    const msg = `Hi! This is ${invoice.CustomerName} reaching out about invoice ${invoice.InvoiceNumber}.`;
    window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <>
      <div className={styles.navWrap}>
        <header className={styles.nav}>
          <div className={styles.navInner}>
            <a className={styles.navLogo} href={`/customerzone${base}`}>
              <img src="/logo-type-white.svg" alt="ServCraft" onError={(e: any) => { e.target.style.display = 'none'; }} />
            </a>
            <div className={styles.navRight}>
              <a className={styles.btnTrial} href="https://servcraft.co.za/sign-up" target="_blank" rel="noopener">
                Start your free trial
              </a>
            </div>
          </div>
        </header>
      </div>
      <div className={styles.root}>
        <div className={styles.page}>
          <div className={styles.wrap}>
            <div className={styles.portalId}>
              <div className={`${styles.portalBadge} ${!logoUrl ? styles.isFallback : ''}`}>
                {logoUrl ? (
                  <img src={logoUrl} alt={companyName} />
                ) : (
                  <span className={styles.portalBadgeFallback}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="3" /><path d="M9 9h.01M15 9h.01M9 15h6" />
                    </svg>
                  </span>
                )}
              </div>
              <p className={styles.portalName}>{companyName}</p>
            </div>
          <div className={styles.card}>
            <div className={styles.cardToolbar}>
              <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                <Link to="/customerzone" search={{ t, c }}>Home</Link>
                {fromJobId ? (
                  <>
                    <span className={styles.bcSep}><IconChevronRight /></span>
                    <Link to="/customerzone" search={{ t, c, tab: 'jobs', expand: '1' }}>Jobs</Link>
                    <span className={styles.bcSep}><IconChevronRight /></span>
                    <Link to="/customerzone/viewjob" search={{ t, c, i: fromJobId }}>{decodeURIComponent(fromJobNum as string)}</Link>
                  </>
                ) : (
                  <>
                    <span className={styles.bcSep}><IconChevronRight /></span>
                    <Link to="/customerzone" search={{ t, c, tab: 'invoices', expand: '1' }}>Invoices</Link>
                  </>
                )}
                <span className={styles.bcSep}><IconChevronRight /></span>
                <span className={styles.bcCurrent}>{invoice.InvoiceNumber}</span>
              </nav>
            </div>

            <div className={styles.cardInner}>
              <div className={styles.detail}>

                <div className={styles.detailHeader}>
                  <h1 className={styles.dId}>{invoice.InvoiceNumber}</h1>
                  <div className={styles.dActions}>
                    {!!payNowLink && amountDue > 0 &&
                      (invoice.InvoiceStatus === Enums.InvoiceStatus.Unpaid || invoice.InvoiceStatus === Enums.InvoiceStatus.Overdue) && (
                      <button className={`${styles.act} ${styles.actPay}`} type="button" onClick={payNow}>
                        <span>Pay Now</span>
                      </button>
                    )}
                    {waPhone && (
                      <button className={styles.act} onClick={handleWhatsApp}>
                        <IconBrandWhatsapp size={14} color="#25d366" /><span>WhatsApp</span>
                      </button>
                    )}
                    <button className={`${styles.act} ${styles.actIconOnly}`} type="button" onClick={() => setShowMessage(true)} title="Leave a comment">
                      <IconMessageCircle size={14} />
                    </button>
                    <button className={styles.act} type="button" onClick={handleDownload} disabled={printing}>
                      {printing
                        ? <IconLoader2 size={14} className={styles.actSpinner} />
                        : <IconPrinter size={14} />}
                      <span>Print</span>
                    </button>
                  </div>
                </div>

                {linkedJob && !fromJobId && (
                  <a
                    className={styles.linkedJob}
                    href={`/customerzone/viewjob${base}&i=${linkedJob.ID}`}
                    onClick={(e) => { e.preventDefault(); router.push(`/customerzone/viewjob${base}&i=${linkedJob.ID}`); }}
                  >
                    <span className={styles.ljIc}>
                      <IconBriefcase size={18} />
                    </span>
                    <span className={styles.ljBody}>
                      <span className={styles.ljEyebrow}>Linked job</span>
                      <span className={styles.ljTitle}>
                        <span className={styles.ljId}>{linkedJob.JobCardNumber}</span>
                        {linkedJob.JobCardStatusDescription && (
                          <StatusPill text={linkedJob.JobCardStatusDescription} tone={deriveStatusTone(linkedJob.JobCardStatusDescription)} />
                        )}
                      </span>
                      {(linkedJob.JobTypeName || linkedJob.Location?.AddressLine1) && (
                        <span className={styles.ljMeta}>
                          {[linkedJob.JobTypeName, linkedJob.Location?.AddressLine1].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </span>
                    <span className={styles.ljCta}>
                      <span>View job</span>
                      <IconChevronRight size={14} />
                    </span>
                  </a>
                )}

                <div className={styles.contact}>
                  {contactName && <span className={styles.cName}>{contactName}</span>}
                  {invoice.CustomerName && contactName !== invoice.CustomerName && (
                    <span className={styles.cLine}>{invoice.CustomerName}</span>
                  )}
                  {invoice.Contact?.MobileNumber && <span className={styles.cLine}>{invoice.Contact.MobileNumber}</span>}
                  {invoice.Contact?.EmailAddress && <span className={styles.cLine}>{invoice.Contact.EmailAddress}</span>}
                  {invoice.Location?.LocationDisplay && <span className={styles.cLine}>{invoice.Location.LocationDisplay}</span>}
                </div>

                <div className={styles.panel}>
                  <div className={styles.panelHead}>
                    <h2 className={styles.panelTitle}>Invoice Detail</h2>
                    <span className={styles.panelBadge}>
                      <StatusPill text={statusLabel} tone={tone} />
                    </span>
                  </div>
                  <div className={styles.totalSummary}>
                    <span className={styles.tsLabel}>Amount Due</span>
                    <span className={styles.tsValue}>{Helper.getCurrencyValue(amountDue, currencySymbol)}</span>
                  </div>
                  <div className={styles.qdBody}>
                    <div className={styles.qdCol}>
                      {invoice.Reference && (
                        <div className={styles.field}>
                          <span className={styles.fLabel}>Reference Number</span>
                          <span className={styles.fValue}>{invoice.Reference}</span>
                        </div>
                      )}
                      <div className={styles.fields}>
                        {invoice.InvoiceDate && (
                          <div className={styles.field}>
                            <span className={styles.fLabel}>Invoice Date</span>
                            <span className={styles.fValue}>{moment(invoice.InvoiceDate).format('D MMM YYYY')}</span>
                          </div>
                        )}
                        {invoice.DueDate && (
                          <div className={styles.field}>
                            <span className={styles.fLabel}>Due Date</span>
                            <span className={styles.fValue}>{moment(invoice.DueDate).format('D MMM YYYY')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {invoice.Comments && (
                      <div className={styles.qdCol}>
                        <div className={styles.field}>
                          <span className={styles.fLabel}>Comments</span>
                          <span className={styles.fValue} style={{ whiteSpace: 'pre-wrap' }}>{invoice.Comments}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.panel}>
                  <h2 className={styles.panelTitle}>Invoice Items</h2>
                  {groupedItems.length > 0 && (
                    <div className={styles.tableWrap}>
                      <table className={styles.tbl}>
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Description</th>
                            <th className={styles.numRight}>Qty</th>
                            <th className={styles.numRight}>Price</th>
                            <th className={styles.tblAmtCol}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedItems.map((group: any) => (
                            <React.Fragment key={group.id}>
                              {group.isSection && !group.hideLineItems && (
                                <tr><td colSpan={5} className={styles.strong} data-label="">{group.name}</td></tr>
                              )}
                              {group.isSection && group.hideLineItems && (
                                <tr>
                                  <td colSpan={4} style={{fontStyle: 'italic'}}>{group.name}</td>
                                  <td className={`${styles.strong} ${styles.tblAmtCol}`}>{Helper.getCurrencyValue(group.subTotal, currencySymbol)}</td>
                                </tr>
                              )}
                              {!group.hideLineItems && group.items.map((item: any) => {
                                const isDesc = item.InvoiceItemType === Enums.InvoiceItemType.Description;
                                return (
                                  <tr key={item.ID || item.InventoryCode}>
                                    <td className={styles.code} data-label="Code">{!isDesc ? item.InventoryCode : ''}</td>
                                    <td data-label="Description">{item.Description}</td>
                                    <td className={styles.numRight} data-label="Quantity">{!isDesc ? item.Quantity : ''}</td>
                                    <td className={styles.numRight} data-label="Price">{!isDesc ? Helper.getCurrencyValue(item.UnitPriceExclusive, currencySymbol) : ''}</td>
                                    <td className={`${styles.strong} ${styles.tblAmtCol}`} data-label="Amount">{!isDesc ? Helper.getCurrencyValue(item.LineTotalExclusive, currencySymbol) : ''}</td>
                                  </tr>
                                );
                              })}
                              {group.isSection && !group.hideLineItems && group.displaySubtotal && (
                                <tr>
                                  <td colSpan={4} className={styles.numRight}><em>Subtotal</em></td>
                                  <td className={`${styles.strong} ${styles.tblAmtCol}`}>{Helper.getCurrencyValue(group.subTotal, currencySymbol)}</td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className={styles.totalsAlign}>
                    <div className={styles.totalsCard}>
                      <div className={styles.totalsRow}>
                        <span>Subtotal Excl VAT</span>
                        <span className={styles.tAmt}>{Helper.getCurrencyValue(invoice.SubTotalExclusive, currencySymbol)}</span>
                      </div>
                      {invoice.DiscountPercentage > 0 && (
                        <div className={styles.totalsRow}>
                          <span>Discount</span>
                          <span className={styles.tAmt}>{invoice.DiscountPercentage}%</span>
                        </div>
                      )}
                      <div className={styles.totalsRow}>
                        <span>Total Excl VAT</span>
                        <span className={styles.tAmt}>{Helper.getCurrencyValue(+invoice.TotalInclusive - +invoice.TotalTax, currencySymbol)}</span>
                      </div>
                      <div className={styles.totalsRow}>
                        <span>VAT {taxPercentage ? `(${taxPercentage}%)` : ''}</span>
                        <span className={styles.tAmt}>{Helper.getCurrencyValue(invoice.TotalTax, currencySymbol)}</span>
                      </div>
                      <div className={`${styles.totalsRow} ${styles.totalsGrand}`}>
                        <span>Total Incl VAT</span>
                        <span className={styles.tAmt}>{Helper.getCurrencyValue(invoice.TotalInclusive, currencySymbol)}</span>
                      </div>
                      {payments.length > 0 && (
                        <div className={styles.totalsRow}>
                          <span>Payments Received</span>
                          <span className={styles.tAmt}>−{Helper.getCurrencyValue(lessPayments, currencySymbol)}</span>
                        </div>
                      )}
                      <div className={styles.totalsRowDue}>
                        <span>Amount Due</span>
                        <span className={styles.totalsAmtDue}>{Helper.getCurrencyValue(amountDue, currencySymbol)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {payments.length > 0 && (
                  <div className={styles.panel}>
                    <h2 className={styles.panelTitle}>Payments</h2>
                    <div className={styles.tableWrap}>
                      <table className={styles.tbl}>
                        <thead>
                          <tr>
                            <th>Payment Date</th>
                            <th>Reference</th>
                            <th>Description</th>
                            <th className={styles.numRight}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((p: any) => (
                            <tr key={p.ID}>
                              <td data-label="Payment Date">{p.PaymentDate ? moment(p.PaymentDate).format('D MMM YYYY') : ''}</td>
                              <td data-label="Reference">{p.Reference}</td>
                              <td data-label="Description">{p.Comment || ''}</td>
                              <td className={`${styles.strong} ${styles.numRight}`} data-label="Amount">{Helper.getCurrencyValue(p.Amount, currencySymbol)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {hasBankDetails && (
                  <div className={styles.panel}>
                    <h2 className={styles.panelTitle}>Banking Details</h2>
                    <dl className={styles.bankList}>
                      {bankName && <><dt>Bank</dt><dd>{bankName}</dd></>}
                      {bankAccountName && <><dt>Account Name</dt><dd>{bankAccountName}</dd></>}
                      {bankAccountNumber && <><dt>Account Number</dt><dd>{bankAccountNumber}</dd></>}
                      {bankBranchCode && <><dt>Branch Code</dt><dd>{bankBranchCode}</dd></>}
                      <dt>Reference</dt><dd>{invoice.InvoiceNumber}</dd>
                    </dl>
                  </div>
                )}

              </div>
            </div>
          </div>

          <div className={styles.support}>
            <span className={styles.sTitle}>Need support?</span>
            <p>Contact us at <a href="mailto:support@servcraft.co.za">support@servcraft.co.za</a></p>
          </div>
          </div>{/* wrap */}
        </div>{/* page */}
      </div>{/* root */}

      {showMessage && (
        <CustomerZoneMessageModal
          module={Enums.Module.Invoice}
          itemID={invoice.ID}
          t={t} c={c} tenant={tenant}
          onClose={() => setShowMessage(false)}
        />
      )}
    </>
  );
}
